import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
  alpha,
  keyframes,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CloudOffRoundedIcon from "@mui/icons-material/CloudOffRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import WifiOffRoundedIcon from "@mui/icons-material/WifiOffRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { classifyApiLoadError } from "../utils/classifyApiLoadError";
import { withActiveWorkspaceQuery } from "../utils/bootstrapWorkspaceFromUrl";
import { useBrand } from "../context/BrandContext";

const softPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.55; }
  50% { transform: scale(1.08); opacity: 0.9; }
`;

/**
 * Tela amigável quando o bootstrap da API falha (ex.: /me/permissions).
 * Diferencia indisponibilidade/offline de problema de permissão.
 */
export default function AppBootstrapErrorState({
  error,
  onRetry,
  isRetrying = false,
  homeTo = "/chat",
}) {
  const { t } = useTranslation("app");
  const brand = useBrand();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const brandName = brand?.name || "Xpro";
  const [browserOffline, setBrowserOffline] = useState(
    () => typeof navigator !== "undefined" && navigator.onLine === false
  );

  useEffect(() => {
    const onOnline = () => {
      setBrowserOffline(false);
      if (typeof onRetry === "function") onRetry();
    };
    const onOffline = () => setBrowserOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [onRetry]);

  const errorCode = String(
    error?.code || error?.response?.data?.code || error?.response?.data?.data?.code || ""
  );
  const isMissingTenantUser =
    errorCode === "errors.auth.platform_no_tenant_user" ||
    errorCode === "errors.auth.partner_no_tenant_user";

  const kind = browserOffline ? "offline" : classifyApiLoadError(error);
  const isPermission = kind === "permission";
  const isSession = kind === "session";
  const isOffline = kind === "offline";
  const isLoad = !isPermission && !isSession && !isOffline;

  const accent = isPermission || isSession
    ? theme.palette.warning.main
    : theme.palette.primary.main;

  const Icon = isPermission || isSession
    ? LockRoundedIcon
    : browserOffline || isOffline
      ? WifiOffRoundedIcon
      : CloudOffRoundedIcon;

  const chipLabel = isPermission
    ? t("appBootstrap.chipPermission")
    : isSession
      ? t("appBootstrap.chipSession")
      : isOffline
        ? t("appBootstrap.chipOffline")
        : t("appBootstrap.chipLoad");

  const title = isPermission
    ? isMissingTenantUser
      ? t("appBootstrap.tenantUserMissingTitle", {
          defaultValue: "Seu usuário ainda não está neste workspace",
        })
      : t("appBootstrap.permissionTitle")
    : isSession
      ? t("appBootstrap.sessionTitle")
      : isOffline
        ? t("appBootstrap.offlineTitle", { brand: brandName })
        : t("appBootstrap.loadTitle", { brand: brandName });

  const body = isPermission
    ? isMissingTenantUser
      ? t("appBootstrap.tenantUserMissingBody", {
          defaultValue:
            "O console da plataforma precisa de um usuário com o mesmo e-mail neste workspace. Peça a um administrador para sincronizar o acesso ou tente novamente após a atualização do sistema.",
        })
      : t("appBootstrap.permissionBody")
    : isSession
      ? t("appBootstrap.sessionBody")
      : isOffline
        ? t("appBootstrap.offlineBody", { brand: brandName })
        : t("appBootstrap.loadBody", { brand: brandName });

  const tip = isPermission
    ? isMissingTenantUser
      ? t("appBootstrap.tenantUserMissingTip", {
          defaultValue: "Abra o workspace de novo pelo console da plataforma após o deploy da correção.",
        })
      : t("appBootstrap.permissionTip", { brand: brandName })
    : isSession
      ? t("appBootstrap.sessionTip")
      : isOffline
        ? t("appBootstrap.offlineTip")
        : t("appBootstrap.loadTip");

  const handleReloadPage = useCallback(() => {
    window.location.reload();
  }, []);

  const handleRetry = useCallback(() => {
    if (typeof onRetry === "function") {
      onRetry();
      return;
    }
    handleReloadPage();
  }, [onRetry, handleReloadPage]);

  return (
    <Box
      role="alert"
      aria-live="polite"
      sx={{
        width: "100%",
        minHeight: "min(72vh, 640px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 5 },
        backgroundImage: `
          radial-gradient(ellipse 80% 50% at 50% -10%, ${alpha(accent, isDark ? 0.22 : 0.14)} 0%, transparent 60%),
          linear-gradient(180deg, ${alpha(theme.palette.background.default, 0)} 0%, ${alpha(
            accent,
            isDark ? 0.04 : 0.03
          )} 100%)
        `,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 480,
          textAlign: "center",
          borderRadius: 4,
          border: `1px solid ${alpha(accent, isDark ? 0.28 : 0.16)}`,
          bgcolor: isDark
            ? alpha(theme.palette.background.paper, 0.92)
            : theme.palette.background.paper,
          boxShadow: isDark
            ? `0 18px 48px ${alpha(theme.palette.common.black, 0.35)}`
            : `0 18px 48px ${alpha(accent, 0.1)}, 0 2px 8px ${alpha(
                theme.palette.common.black,
                0.04
              )}`,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            height: 4,
            width: "100%",
            background: `linear-gradient(90deg, ${alpha(accent, 0.15)}, ${accent}, ${alpha(
              accent,
              0.15
            )})`,
          }}
        />

        <Box sx={{ px: { xs: 3, sm: 4.5 }, pt: 4, pb: { xs: 3.5, sm: 4 } }}>
          <Box
            sx={{
              position: "relative",
              mx: "auto",
              mb: 2.75,
              width: 88,
              height: 88,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                border: `1.5px solid ${alpha(accent, isDark ? 0.35 : 0.22)}`,
                animation: `${softPulse} 2.8s ease-in-out infinite`,
              }}
            />
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 10,
                borderRadius: "50%",
                bgcolor: alpha(accent, isDark ? 0.16 : 0.1),
              }}
            />
            <Box
              sx={{
                position: "relative",
                width: 56,
                height: 56,
                borderRadius: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(accent, isDark ? 0.22 : 0.14),
                color: accent,
                boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, isDark ? 0.08 : 0.55)}`,
              }}
            >
              <Icon sx={{ fontSize: 30 }} />
            </Box>
          </Box>

          <Chip
            size="small"
            label={chipLabel}
            sx={{
              mb: 1.75,
              fontWeight: 600,
              letterSpacing: 0.2,
              bgcolor: alpha(accent, isDark ? 0.18 : 0.1),
              color: isDark ? theme.palette.text.primary : accent,
              border: `1px solid ${alpha(accent, isDark ? 0.3 : 0.2)}`,
              "& .MuiChip-label": { px: 1.25 },
            }}
          />

          <Typography
            variant="h5"
            component="h1"
            fontWeight={700}
            sx={{
              letterSpacing: "-0.02em",
              lineHeight: 1.25,
              mb: 1.25,
              color: "text.primary",
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              lineHeight: 1.65,
              mb: 1.5,
              maxWidth: 380,
              mx: "auto",
              fontSize: "0.98rem",
            }}
          >
            {body}
          </Typography>

          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: "block", mb: 3, lineHeight: 1.5, maxWidth: 340, mx: "auto" }}
          >
            {tip}
          </Typography>

          <Stack spacing={1.25} alignItems="center">
            {isSession ? (
              <Button
                variant="contained"
                color="primary"
                component={RouterLink}
                to="/login"
                startIcon={<LoginRoundedIcon />}
                sx={{
                  fontWeight: 700,
                  borderRadius: 2.5,
                  px: 3.5,
                  py: 1.15,
                  minWidth: 220,
                  textTransform: "none",
                  boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.28)}`,
                }}
              >
                {t("appBootstrap.goLogin")}
              </Button>
            ) : isPermission ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  component={RouterLink}
                  to={withActiveWorkspaceQuery(homeTo)}
                  startIcon={<HomeRoundedIcon />}
                  sx={{
                    fontWeight: 700,
                    borderRadius: 2.5,
                    px: 3.5,
                    py: 1.15,
                    minWidth: 220,
                    textTransform: "none",
                    boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.28)}`,
                  }}
                >
                  {t("appBootstrap.goHome")}
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  size="small"
                  startIcon={
                    isRetrying ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <RefreshRoundedIcon fontSize="small" />
                    )
                  }
                  onClick={handleRetry}
                  disabled={isRetrying}
                  sx={{
                    fontWeight: 600,
                    textTransform: "none",
                    color: "text.secondary",
                    "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.04) },
                  }}
                >
                  {isRetrying ? t("appBootstrap.retrying") : t("appBootstrap.retry")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    isRetrying ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RefreshRoundedIcon />
                    )
                  }
                  onClick={handleRetry}
                  disabled={isRetrying}
                  sx={{
                    fontWeight: 700,
                    borderRadius: 2.5,
                    px: 3.5,
                    py: 1.15,
                    minWidth: 220,
                    textTransform: "none",
                    boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.28)}`,
                  }}
                >
                  {isRetrying ? t("appBootstrap.retrying") : t("appBootstrap.retry")}
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  size="small"
                  onClick={handleReloadPage}
                  sx={{
                    fontWeight: 600,
                    textTransform: "none",
                    color: "text.secondary",
                    "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.04) },
                  }}
                >
                  {t("appBootstrap.reloadPage")}
                </Button>
              </>
            )}
          </Stack>

          {(isOffline || isLoad) && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 2.5,
                color: "text.disabled",
                letterSpacing: 0.2,
              }}
            >
              {brandName}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
